<script setup>
    import { ref, computed, inject, defineEmits } from "vue";
    import { useMutation, useQueryClient } from "@tanstack/vue-query";
    import { ElInput, ElButton } from "element-plus";
    import { useQuery } from "@tanstack/vue-query";
    import axios from "axios";
    import underWriteMeLogo from "../../assets/images/underWriteMeLogo3.png";

    const userService = inject('UserService');

    const queryClient = useQueryClient();

    const { data: currentIntegration, isLoading: isLoadingCurrent } = useQuery({
        queryKey: ["underWriteMe-integration"],
        queryFn: () =>
            axios("api/Integration/GetUnderWriteMeIntegration").then((res) => res.data),
    });

    const { mutate: deleteUserIntegration, isLoading: isDeleting } = useMutation({
        mutationFn: () =>
            axios.delete("api/Integration/DeleteUserIntegration/UNDERWRITEME"),
        onSuccess: () => {
            underWriteMeCredentials.value.userName = "";
            queryClient.invalidateQueries(["underWriteMe-integration"]);
            queryClient.invalidateQueries(["user-integrations"]);
            userService.update();
        },
    });

    const underWriteMeCredentials = ref({
        userName: "",
    });

    const emit = defineEmits(["saveCredentials"]);

    const { mutate: saveCredentials, isLoading: isSaving, isError } = useMutation({
        mutationFn: () =>
            axios
                .post(
                    "api/Integration/SaveUnderWriteMeCredentials",
                    underWriteMeCredentials.value
                ),
        onSuccess: async () => {
            queryClient.invalidateQueries(["underWriteMe-integration"]);
            queryClient.invalidateQueries(["user-integrations"]);
            await userService.update();
            emit("saveCredentials", "UNDERWRITEME");
        }
    });

    const isLoading = computed(
        () => isSaving.value || isLoadingCurrent.value || isDeleting.value
    );
    const disabled = computed(() => !!currentIntegration.value || isLoading.value);

</script>

<template>
    <div class="integration-section" style="background-color: #fae5d7;">
        <div class="right-section">
            <div class="description">
                <p>
                    To ensure the prices and quotes we obtain from UnderwriteMe are the prices and products you
                    have access to.
                </p>
                <p> please provide us with the email address you use to log in to
                    UnderwriteMe’s Protection Platform.
                </p>
            </div>
            <div class="form-section">
                <div class="form-card">
                    <div class="image-logo">
                        <img alt="underwriteMe" :src="underWriteMeLogo" />
                    </div>
                    <div class="form-group">
                        <div class="form-row">
                            <label for="email"> Email address </label>
                        </div>

                        <ElInput v-if="!!currentIntegration" id="WebID" v-model="currentIntegration.userName" disabled />
                        <ElInput v-else id="WebID" v-model="underWriteMeCredentials.userName" :min-length="1" type="text" :class="{ error : isError}" />
                        <span v-if="isError" class="errorText"> Sorry, we are unable to match your email to an UnderwriteMe account, please check and try again.</span>
                    </div>
                    <div class="form-group">
                        <div class="form-row">
                            <ElButton v-if="!!currentIntegration" type="danger" @click="deleteUserIntegration" :loading="isLoading">
                                Unlink account
                            </ElButton>
                            <ElButton v-else type="primary" @click="saveCredentials" :loading="isLoading" :disabled="disabled">
                                Link account
                            </ElButton>
                        </div>
                    </div>
                    <div class="help-links">
                        <P>
                            <strong>Don’t have an UnderWriteMe account? </strong>
                            <a target="_blank" href="https://www.underwriteme.co.uk/adviser-registration-form/">
                                Click Here
                            </a>
                            to set up an account.
                        </P>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
    .errorText {
        color: red;
        font-size: small;
    }

    .form-section {
        width: 350px;
        background-color: #ffffff;
        padding: 10px;
    }

    .form-group {
        margin-bottom: 1rem;
    }

    .form-card {
        background-color: #ffffff;
        padding: 15px;
        box-shadow: 0 2px 12px 0 rgba(0,0,0,.1);
    }

    .form-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.25rem;
    }
        .form-row label {
            font-size: 0.875rem;
        }
    .right-section {
        flex: 1;
        display: flex;
    }

        .right-section .description {
            flex: 1;
            padding-right: 2rem;
            font-size: 0.875rem;
        }

    .help-links {
        margin-top: 1rem;
        font-size: 0.75rem;
    }
        .help-links p {
            margin-bottom: 0.25rem;
            font-weight: 600;
        }
    .image-logo {
        background: white;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
    }

        .image-logo img {
            max-width: 140px;
            max-height: 30px;
            object-fit: cover;
            margin-bottom: 5px;
        }
</style>

<style>
    .error .el-input__wrapper {
        border: 1px solid red;
    }
</style>
